import { client } from "@lib/api"
import { SupportedBank } from "./useSupportedBank"
import { useCallback, useEffect, useState } from "react"
import { AddBankParams, EditBankParams, GetBankingResponse, RedemptionOption } from "../types/redemption"
import * as Sentry from "@sentry/react"

const useBankManagement = (fetchBanks: boolean = true) => {
    const addBank = async ({
        account_nickname,
        asset = "idr",
        source = "REDEMPTION",
        bank_name,
        bank_account,
    }: AddBankParams) => {
        try {
            return await client.doPost('/v2/wallet/banking', {
                account_nickname,
                asset,
                source,
                bank_name,
                bank_account,
            })
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const editBank = async ({
        bank_uuid,
        account_nickname,
        asset = "idr",
        source = "REDEMPTION",
        bank_name,
        bank_account,
    }: EditBankParams) => {
        try {
            return await client.doPost(`/v2/wallet/banking/${bank_uuid}/update`, {
                account_nickname,
                asset,
                bank_name,
                bank_account,
                source,
            })
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const removeBank = async ({
        bank_uuid
    }: {
        bank_uuid: string
    }) => {
        try {
            await client.doDelete(`/wallet/banking/${bank_uuid}/`)
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const validateBank = async ({
        bank_uuid
    }: {
        bank_uuid: string
    }) => {
        try {
            return await client.doGet(`/v2/wallet/banking/${bank_uuid}/validation`)
        } catch (error) {   
            Sentry.captureException(error)
            throw error
        }
    }

    const [banks, setBanks] = useState<GetBankingResponse[]>([])
    const [bankAsRedemptionOption, setBankAsRedemptionOption] = useState<RedemptionOption[]>([])
    const [supportedBanks, setSupportedBanks] = useState<SupportedBank[]>([])
    const [localLoading, setLocalLoading] = useState(true);

    const getDataBase = async () => {
        try {
            setLocalLoading(true)
            const banks = await client.doGet('/wallet/bankings')
            const supportedBanks: SupportedBank[] = await client.doGet('/v2/wallet/supported-banks')
            const bankAsRedemptionOption: RedemptionOption[] = banks.map((bank: GetBankingResponse) => {
                return {
                    id: bank.bank_uuid,
                    name: bank.bank_account_name,
                    iconUrl: supportedBanks?.find(supportedBank => supportedBank.code === bank.bank_name)?.icon_url || '',
                    accountNumber: bank.bank_account,
                    provider: supportedBanks?.find(supportedBank => supportedBank.code === bank.bank_name)?.display_name || '',
                    nickname: bank.account_nickname,
                    isValid: bank.status === 'VALIDATED',
                }
            })
            setSupportedBanks(supportedBanks)
            setBanks(banks)
            setBankAsRedemptionOption(bankAsRedemptionOption)
        } catch (error) {
            Sentry.captureException(error)
        } finally {
            setLocalLoading(false)
        }
    }

    const getData = useCallback(() => {
        getDataBase()
    }, [])

    useEffect(() => {
        if (!fetchBanks) return
        getData()
    }, [fetchBanks])
    
    return {
        addBank,
        editBank,
        removeBank,
        validateBank,
        banks: banks as GetBankingResponse[],
        bankAsRedemptionOption,
        supportedBanks,
        isLoading: localLoading,
        reload: getDataBase,
    }
}

export default useBankManagement
